import { captureException } from '@sentry/react';
import { UseMutationResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  createAccount,
  deleteAccount,
  exportXmlAccounts,
  fetchAccountBankDetailsUrl,
  fetchAccountImportConfig,
  fetchAccountImportTemplates,
  fetchAllAccounts,
  fetchBalances,
  importTransactions,
  requestAcceptCashbackTermsAndCondition,
  requestDeletionAccount,
  updateAccount,
} from '~/api/accounts';
import { snack } from '~/hooks/ToastProvider';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import { useAnalytics } from '~/hooks/useAnalytics';
import { AnalyticsEvents } from '~/services/analytics';
import {
  Account,
  AccountCreateRequestPayload,
  AccountFilters,
  AccountQueryKeys,
  AccountsFilters,
} from '~/types/account';
import { ConsentQueryKeys } from '~/types/consent';
import { assertIsDefined, isDefined } from '~/types/typeGuards';
import { downloadBlob } from '~/utils/download';

export const useAllAccountsQuery = (filters?: AccountsFilters) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: AccountQueryKeys.company(companyId, filters),
    queryFn: () => fetchAllAccounts(companyId, filters),
    enabled: !!companyId,
    staleTime: Infinity,
  });
};

export const useAccountBalances = (filter: AccountFilters) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: AccountQueryKeys.balances(companyId, filter),
    queryFn: () => fetchBalances({ companyId, ...filter }),
    enabled: !!companyId,
  });
};

export const useNonHiddenAccountsQuery = () => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: AccountQueryKeys.company(companyId),
    queryFn: () => fetchAllAccounts(companyId),
    enabled: !!companyId,
    staleTime: Infinity,
    select: (data) => data.filter((el) => !el.hiddenAt),
  });
};

export const useCreateAccountMutation = () => {
  const queryClient = useQueryClient();
  const companyId = useActiveCompanyId();

  return useMutation({
    mutationFn: (params: Omit<AccountCreateRequestPayload, 'company'>) =>
      createAccount({ ...params, company: { id: companyId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AccountQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: ConsentQueryKeys.all });
    },
  });
};

export const useUpdateAccountMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AccountQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: ConsentQueryKeys.all });
    },
  });
};

export const useDownloadBankDetails = (): UseMutationResult<string, Error, Account['id']> => {
  return useMutation({
    mutationFn: (accountId: Account['id']) => fetchAccountBankDetailsUrl(accountId),
  });
};

export const useDeleteAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AccountQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: ConsentQueryKeys.all });
    },
  });
};

export const useRequestDeletionAccount = () => {
  return useMutation({
    mutationFn: (request: { account: Account['id']; reason: string; more?: string }) =>
      requestDeletionAccount(request.account, request.reason, request.more),
  });
};

export const useAcceptCashbackTermsAndCondition = () => {
  return useMutation({
    mutationFn: (request: { account: Account['id'] }) => requestAcceptCashbackTermsAndCondition(request.account),
    onError: (error) => captureException(error),
  });
};

export const useAccountQuery = (id: Account['id'] | null) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: AccountQueryKeys.company(companyId),
    queryFn: () => fetchAllAccounts(companyId),
    enabled: !!companyId && !!id,
    staleTime: Infinity,
    select: (data) => data.find((el) => el.id === id),
  });
};

export const useExportAccounts = () => {
  const companyId = useActiveCompanyId();
  const { t } = useTranslation();
  const { track } = useAnalytics();

  return useMutation({
    mutationFn: async () => await exportXmlAccounts(companyId),
    onError: () => snack.error(t('ERROR_SUBMITTING_FLOW')),
    onSuccess: ({ data }) => downloadBlob(data, 'Conti.xlsx'),
    onSettled: () => track(AnalyticsEvents.BANK_ACCOUNT_EXPORTED),
  });
};

export const useAccountImportTemplates = (accountId?: Account['id'] | null) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: AccountQueryKeys.importTemplates(companyId, accountId ?? ''),
    queryFn: () => {
      assertIsDefined(accountId, 'accountId must be defined');
      return fetchAccountImportTemplates(accountId);
    },
    enabled: Boolean(companyId) && isDefined(accountId),
  });
};

export const useAccountImportConfig = () => {
  return useMutation({
    mutationFn: fetchAccountImportConfig,
  });
};

export const useAccountImportTransactions = () => {
  return useMutation({
    mutationFn: importTransactions,
  });
};
